import { ChangeContext, LabelType } from '@angular-slider/ngx-slider'
import { CurrencyPipe } from '@angular/common'
import { AfterContentInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { addDays, format, isAfter, isSameDay, parseISO } from 'date-fns'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { concatMap, debounceTime, filter, take, takeUntil } from 'rxjs/operators'
import { environment } from '../../../../../../environments/environment'
import {
    AssetType, BookingConfirmationFlow,
    Commission,
    CommissionFees,
    CurrencyAmount,
    DailyAllocationType,
    Fees, 
    GuestCount,
    Listing,
    ListingAvailabilityCalendarGQL,
    ListingTravelDatesByIdGQL,
    ListingTravelDatesFetchCommissionGQL,
    ListingTravelDatesGQL,
    PriceRange
} from '../../../../../../graphql'
import { Auth, ScopeManager, TenantService } from '../../../../../injectable'
import { CurrencyService } from '../../../../../injectable/currency-service'
import { removeTypeNames, toLocalDate, UnsubscribeOnDestroy } from '../../../../../utility'
import { extractUnavailableDays } from '../../calendar/calendar-utility'
import { ActiveWindow } from '../shared/active-window'
import { TravelDates } from '../shared/travel-dates'

@Component({
    selector: 'app-listing-travel-dates',
    templateUrl: 'listing-travel-dates.html',
    styleUrls: ['listing-travel-dates.scss'],
    providers: [CurrencyPipe]
})
export class ListingTravelDatesComponent extends UnsubscribeOnDestroy implements AfterContentInit, OnChanges {

    @Input() listingId: string
    @Input() travelDates: TravelDates
    @Input() minimumNights: number | undefined
    @Input() businessCustomerReference: string
    @Input() guestCriteria$: Observable<Array<GuestCount>>
    @Input() maxGuestsExceeded = false
    @Input() consumerView = true

    /**
     * Showing available inventory will show "X rooms left" on this
     * travel dates bar. For example; Given 3 days, where
     * day 1 and 2 have 2 availability and day 3 has 1 availability,
     * this will show '1 room left' - the lowest availability of the 3 days.
     */
    @Input() showAvailableInventory = false

    /**
     * Availability is specifically allocated per day. The period between the earliest
     * and the latest available days is called the ActiveWindow.
     */
    activeWindow: ActiveWindow
    unavailableDays: Date[] = []
    roomsLeft = 0
    tomorrow: Date = addDays(new Date(), 7)
    fees: Fees | undefined
    priceRange: PriceRange
    allowInstantBook: boolean

    private guestCriteria: Array<GuestCount>
    private bookingPanelCurrency = environment.tenant.currency
    commission: Commission
    cashCurrency: string

    originalPrice: number
    price: number
    editablePrice: number
    formattedPrice: string
    adjustedCommissionRate: string

    maxAllowedPrice: number
    maxAllowedPercentage: string
    minAllowedPrice: number
    minAllowedPercentage: string
    sliderOptions: object | null
    useManualPriceAdjustmentInput = false
    adjustedPriceValid = false

    authenticated: boolean
    isOwnListing: boolean
    assetType: AssetType | undefined
    AssetType = AssetType
    completeAvailabilityCalendarAvailable = true

    commissionFees: CommissionFees
    nightlyCommissionRate$: Observable<CurrencyAmount>
    totalCommissionRate$: Observable<CurrencyAmount>
    nightlyPrice$: Observable<CurrencyAmount>
    effectiveFees$: Observable<Fees>

    formGroup: FormGroup
    travelDatesAreValid = false
    loadingPrices = false
    showErrorPrompt = false
    showUserRemediableErrors = false
    userRemediableErrors: Array<string> = []

    private sliderPrice$ = new BehaviorSubject<number | null>(null)
    private originalFees: Fees
    private adjustedPrice: CurrencyAmount
    private presentmentCurrencySubscription: Subscription

    @Output() update = new EventEmitter<TravelDates>()
    @Output() reserve = new EventEmitter<SelectedTravel>()
    @Output() availableFrom = new EventEmitter<string>()
    @Output() rate = new EventEmitter<string>()
    @Output() commissionIsSet = new EventEmitter<boolean>()
    @Output() updateFees = new EventEmitter<Fees>()

    constructor(private _auth: Auth,
                private _scopeManager: ScopeManager,
                private _tenantService: TenantService,
                private _listingAvailabilityCalendarGQL: ListingAvailabilityCalendarGQL,
                private _listingTravelDatesGQL: ListingTravelDatesGQL,
                private _listingTravelDatesByIdGQL: ListingTravelDatesByIdGQL,
                private _currencyService: CurrencyService,
                private _fetchCommissionGQL: ListingTravelDatesFetchCommissionGQL,
                private _currencyPipe: CurrencyPipe,
    ) {
        super()

        this._auth.authenticatedSubject
            .pipe(takeUntil(this.destroy$))
            .subscribe(authenticated => {
                this.authenticated = authenticated
            })

        this.update
            .pipe(takeUntil(this.destroy$))
            .subscribe(travelDates => {
                this.sliderOptions = null
                if (!travelDates?.checkin || !travelDates?.checkout) {
                    this._listingTravelDatesByIdGQL
                        .fetch({
                            listingId: this.listingId
                            }, { fetchPolicy: 'no-cache' })
                        .subscribe(result => { 
                            this.handleListingQuery(result) 
                        })
                } else {
                    this._listingTravelDatesGQL
                        .fetch({
                            checkin: format(travelDates?.checkin, 'yyyy-MM-dd'),
                            checkout: format(travelDates?.checkout, 'yyyy-MM-dd'),
                            listingId: this.listingId,
                            guestCounts: this.guestCriteria
                        }, { fetchPolicy: 'no-cache' })
                        .subscribe(result => {
                            this.showErrorPrompt = false
                            this.userRemediableErrors = []
                            this.showUserRemediableErrors = false
                            this.handleListingQuery(result)
                        }, (error: any) => {
                            this.userRemediableErrors = error.graphQLErrors
                                .filter(e => e.extensions.classification === 'UserRemediable')
                                .flatMap(e => e.extensions.violations
                                    .map(v => v.instructionsToUser))

                            if (this.userRemediableErrors.length > 0) { 
                                this.showUserRemediableErrors = true 
                            }
                        })
                }
            })

        this.sliderPrice$
            .pipe(
                debounceTime(500), 
                filter(_ => this?.fees?.bookingFee != null && this.commission != null), 
                takeUntil(this.destroy$)
            )
            .subscribe(_ => {
                this.queryCustomCommission()
            })
    }

    ngAfterContentInit() {
        // TODO: LEX-1790 Move Hotel's minimumStay/maximumStay to Listing.stayDurationConditions
        if (this.minimumNights === undefined) {
            this.minimumNights = this._tenantService.minimumStayNights()
        }

        this.guestCriteria$
            // this is already debounced in the guest selection component
            .pipe(takeUntil(this.destroy$))
            .subscribe(criteria => {
                this.loadingPrices = true
                this.guestCriteria = criteria
                this.update.next(this.travelDates)
            })

        this.formGroup = new FormGroup({
            checkin: new FormControl(this.travelDates?.checkin, Validators.required),
            checkout: new FormControl(this.travelDates?.checkout, Validators.required)
        })

        this._scopeManager.hasResourceScope(this.listingId, 'read:listing').subscribe(hasRequiredScope => {
            this.isOwnListing = hasRequiredScope
        })

        this.getAvailability()
    }

    getAvailability(date?: Date) {
        this.loadingPrices = true

        this._listingAvailabilityCalendarGQL
            .fetch({
                input: {
                    listingId: this.listingId,
                    month: format(date ?? this?.travelDates?.checkin ?? new Date(), 'yyyy-MM')
                }}, { fetchPolicy: 'no-cache' })
            .subscribe(result => {
                if (!result.loading) {

                    // Had to use any here to keep compiler happy.
                    const dailyAllocations: any = result?.data?.availabilityCalendar?.availabilityCalendar?.dailyAllocations
                    if (Array.isArray(dailyAllocations)) {

                        if (this.showAvailableInventory) {
                            const roomsLeft = this.calculateRoomsLeft(dailyAllocations)
                            this.roomsLeft = roomsLeft ? roomsLeft : 0
                        }

                        this.unavailableDays = extractUnavailableDays(dailyAllocations)
                        this.activeWindow = new ActiveWindow(dailyAllocations.map(dailyAllocation => parseISO(dailyAllocation.day)))
                        this.completeAvailabilityCalendarAvailable = format(this.activeWindow.end, 'yyyy-MM') === result?.data?.availabilityCalendar?.lastMonthWithAvailability
                        this.formGroup.updateValueAndValidity()

                        const availableFrom = dailyAllocations.find(a =>
                            a.allocated > a.consumed && (
                                isSameDay(new Date(a.day), this.tomorrow) ||
                                isAfter(new Date(a.day), this.tomorrow)
                            )
                        )

                        this.availableFrom.emit(availableFrom?.day)
                    }

                    this.update.emit(this.travelDates)
                }
            })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.businessCustomerReference?.currentValue !== changes?.businessCustomerReference?.previousValue) {
            this.businessCustomerReference = changes.businessCustomerReference.currentValue

            if (!this.shouldShowErrorPrompt()) {
                this.showErrorPrompt = false
            }
        }

        if (!('listingId' in changes)) { return }

        if (changes.listingId.isFirstChange()) { return }

        if (changes.listingId.currentValue !== changes.listingId.previousValue) {
            this.loadingPrices = true
            this.listingId = changes.listingId.currentValue
            this.getAvailability(this.travelDates.checkin)
        }
    }

    // Public
    handleRangeSelected() {
        const travelDates = new TravelDates(this.formGroup.controls.checkin.value, this.formGroup.controls.checkout.value)
        this.update.next(travelDates)
    }

    shouldShowErrorPrompt() {
        return !this.formGroup?.valid || !this.businessCustomerReference || !this.travelDatesAreValid || this.maxGuestsExceeded || this.showUserRemediableErrors
    }

    hideErrorPrompt() {
        this.showErrorPrompt = false
    }

    save() {
        if (this.shouldShowErrorPrompt()) {
            this.showErrorPrompt = true
            return
        } 

        const price = removeTypeNames(this.adjustedPrice ? this.adjustedPrice : this.originalFees.bookingFee)
        const priceInListingCurrency = this.getFormattedPrice(price.amountMicros / 1_000_000, price.currency)
        const bookingConfirmationFlow = this.allowInstantBook ? BookingConfirmationFlow.InstantBook : BookingConfirmationFlow.RequestToBook

        this.reserve.emit(new SelectedTravel(
            this.totalCommissionRate$ ,
            this.adjustedCommissionRate,
            this.formattedPrice,
            priceInListingCurrency,
            this.listingId,
            this.travelDates,
            price,
            bookingConfirmationFlow,
            this?.fees?.bookingToken
        ))
    }

    manualPriceUpdate(target: EventTarget) {
        if (!(target instanceof HTMLInputElement)) { return }
        this.adjustedPriceValid = this.adjustedPriceIsValid(target.valueAsNumber)
        if (this.adjustedPriceValid) {
            this.price = target.valueAsNumber
            this.rate.next(this.getFormattedPrice(this.price))
        }
    }

    manualAdjustmentActiveState(active: boolean, target: EventTarget) {
        if (!(target instanceof HTMLInputElement)) { return }
        this.adjustedPriceValid = this.adjustedPriceIsValid(target.valueAsNumber)
        this.useManualPriceAdjustmentInput = active
    }

    adjustFees() {
        this.sliderPrice$.next(this.price)

        if (this.travelDates.nights) {
            this.nightlyPrice$ = this._currencyService.convertToPresentmentCurrency({
                amountMicros: this.price / this.travelDates.nights,
                currency: this._currencyService.getPresentmentCurrency()
            })
        }
    }

    queryCustomCommission() {
        this.rate.next(this.getFormattedPrice(this.price))
        const adjustedBookingFee = {
            amountMicros: Math.round(this.price) * 1_000_000,
            currency: this._currencyService.getPresentmentCurrency(),
        }
        const originalBookingFee = {
            currency: this.originalFees.bookingFee.currency,
            amountMicros: this.originalFees.bookingFee.amountMicros
        } as CurrencyAmount

        this._currencyService
            .convert(originalBookingFee.currency, adjustedBookingFee)
            .pipe(
                take(1),
                concatMap((adjustedPrice) => {
                    return this._fetchCommissionGQL
                        .fetch({
                            listingId: this.listingId,
                            adjustedPrice: this.clampPrice(adjustedPrice),
                            originalPrice: originalBookingFee
                        })
                })
            )
            .subscribe(result => {
                if (result.loading) { return }

                this.commissionFees = result.data.commission as CommissionFees
                this.adjustedCommissionRate = this.commissionFees.commissionRate.toString()

                if (this.travelDates.nights) {
                    this.nightlyCommissionRate$ = this._currencyService.convertToPresentmentCurrency({
                        currency: this.commissionFees.commission.currency,
                        amountMicros: this.commissionFees.commission.amountMicros / this.travelDates.nights
                    })
                }

                this.totalCommissionRate$ = this._currencyService.convertToPresentmentCurrency(this.commissionFees.commission)
                this.loadingPrices = false
            })

        // Update UI
        this.formattedPrice = this.getFormattedPrice(this.price)
        this.editablePrice = Math.round(this.price)
    }

    userChange($event: ChangeContext) {
        this.sliderPrice$.next($event.value)
    }

    newSearchDates(dates: { checkin: Date, checkout: Date }): void {
        if (dates.checkin && dates.checkout) {
            this.loadingPrices = true
        }
        
        this.formGroup.patchValue({
            checkin: dates.checkin,
            checkout: dates.checkout
        })

        this.handleRangeSelected()
    }

    datesAreValid(isValid) {
        this.travelDatesAreValid = isValid
    }

    private getFormattedPrice(value: number, currency?: string): string {
        const formatedPrice = this._currencyPipe.transform(value, currency || this._currencyService.getPresentmentCurrency(), 'symbol-narrow', '1.0-0')
        return formatedPrice || ''
    }

    private adjustedPriceIsValid(value: number) {
        return value >= this.minAllowedPrice && value <= this.maxAllowedPrice
    }

    private setupPresentmentCurrency(): void {
        this?.presentmentCurrencySubscription?.unsubscribe()
        this.presentmentCurrencySubscription = this._currencyService
            .batchConvertToPresentmentCurrency([
                {
                    amountMicros: this.originalPrice,
                    currency: this.bookingPanelCurrency
                },
                {
                    amountMicros: this.price,
                    currency: this.bookingPanelCurrency
                },
                {
                    amountMicros: this.minAllowedPrice,
                    currency: this.bookingPanelCurrency
                },
                {
                    amountMicros: this.maxAllowedPrice,
                    currency: this.bookingPanelCurrency
                },
            ])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([price,
                            originalPrice,
                            minValue,
                            maxValue]) => {
                this.price = price.amountMicros
                this.rate.next(this.getFormattedPrice(this.price))
                this.originalPrice = originalPrice.amountMicros
                this.minAllowedPrice = minValue.amountMicros
                this.maxAllowedPrice = maxValue.amountMicros
                this.sliderOptions = this.createSliderOptions()
                this.bookingPanelCurrency = price.currency
                this.adjustFees()
            })
    }

    private setPrices(): void {
        this.price = this.fees?.bookingFee?.amountMicros / 1_000_000
        this.originalPrice = this.price
        const min = this.getMinAllowedPrice()
        const max = this.getMaxAllowedPrice()
        this.cashCurrency = max.currency
        this.maxAllowedPrice = max.value
        this.maxAllowedPercentage = max.percentage + '%'
        this.minAllowedPrice = min.value
        this.minAllowedPercentage = min.percentage + '%'
    }

    private getMaxAllowedPrice(): { value: number, percentage: number, currency: string } {
        const maxPercentage = this._tenantService.maximumPriceModificationPercentage()
        const maxValueIncrease = this.price * maxPercentage / 100
        const maxAllowedPrice = this.price + maxValueIncrease

        return {
            value: maxAllowedPrice,
            percentage: maxPercentage,
            currency: this.fees?.bookingFee?.currency || ''
        }
    }

    private getMinAllowedPrice(): { value: number, percentage: number, currency: string } {
        const minAllowedPrice = this.price * (1 - this.commission.rate / 100)

        return {
            value: minAllowedPrice,
            percentage: this.commission.rate,
            currency: this.fees?.bookingFee?.currency || ''
        }
    }

    private sliderLabel(label: string, value: number, classNames?: string): string {
        return `<div class="_flex _flex-col _text-sm ${classNames}"><span class="_font-normal">${label}</span><span>${this.getFormattedPrice(value)}</span></div>`
    }

    private createSliderOptions(): object {
        return {
            floor: this.minAllowedPrice,
            ceil: this.maxAllowedPrice,
            ticksArray: [this.originalPrice],
            translate: (value: number, label: LabelType): string => {
                switch (label) {
                    case 0:
                        return this.sliderLabel('Client pays', value, '_text-primary-1 _font-bold' )
                    case 2:
                        return this.sliderLabel('Discount', value, '_text-primary-1-tint-50 _flex-col-reverse _items-start')
                    case 3:
                        return this.sliderLabel('Premium', value, '_text-primary-1-tint-50 _flex-col-reverse _items-end')
                    default: {
                        return this.getFormattedPrice(value)
                    }
                }
            }
        }
    }

    private calculateRoomsLeft(dailyAllocations: DailyAllocationType[]) {
        if (!this.travelDates?.checkin || !this.travelDates?.checkout) {
            return
        }

        const checkin = toLocalDate(this.travelDates.checkin)
        const checkout = toLocalDate(this.travelDates.checkout)

        let roomsLeft: number | undefined

        dailyAllocations
            .filter(allocation => allocation.day >= checkin && allocation.day < checkout)
            .forEach(allocation => {
                if (!roomsLeft || (allocation.allocated && allocation.allocated < roomsLeft)) {
                    if (allocation.allocated && allocation.consumed) {
                        roomsLeft = allocation.allocated - allocation.consumed
                    }
                }
            })

        return roomsLeft
    }

    private handleListingQuery(result: any) {
        if (result.loading) { return }
        const listing = result?.data?.listingByTravelDetails ?? result?.data?.listing as Listing
        this.assetType = listing?.asset?.assetType
        this.fees = (listing?.fees ?? {
            bookingFee: listing.priceRange?.lowest.bookingFee,
        } as Fees) as Fees
        this.originalFees = this.fees as Fees
        this.commission = listing.commission as Commission
        this.minimumNights = listing.stayDurationConditions.minimum
        this.bookingPanelCurrency = this.fees?.bookingFee?.currency || this.bookingPanelCurrency
        this.allowInstantBook = listing.allowInstantBook
        
        if (this.commission) {
            this.commissionIsSet.next(true)
            this.setPrices()
            this.sliderOptions = this.createSliderOptions()
            this.setupPresentmentCurrency()
        } else {
            this.updateFees.next(this.fees)
            this.loadingPrices = false
        }
    }

    private clampPrice(adjustedPrice: CurrencyAmount) {
        let adjustedPriceMicros = adjustedPrice.amountMicros
        const bookingFeeMicros = this.originalFees.bookingFee.amountMicros
        const minAllowedPrice = Math.ceil(bookingFeeMicros * (1 - this.commission?.rate / 100))
        const maxPercentage = this._tenantService.maximumPriceModificationPercentage()
        const maxValueIncrease = bookingFeeMicros * maxPercentage / 100
        const maxAllowedPrice = Math.floor(bookingFeeMicros + maxValueIncrease)

        adjustedPriceMicros = Math.min(maxAllowedPrice, adjustedPriceMicros)
        adjustedPriceMicros = Math.max(minAllowedPrice, adjustedPriceMicros)
        this.adjustedPrice = {
            currency: adjustedPrice.currency,
            amountMicros: Math.round(adjustedPriceMicros)
        }
        return this.adjustedPrice
    }

    monthSelected($event: Date) {
        if (this.activeWindow.end <= $event || this.activeWindow.start >= $event) {
            this.getAvailability($event)
        }
    }
}

export class SelectedTravel {
    constructor(
        public commissionAmount: Observable<CurrencyAmount>,
        public commisionRate: string,
        public price: string,
        public priceInListingCurrency: string,
        public listingId: string,
        public travelDates: TravelDates,
        public adjustedPrice: CurrencyAmount,
        public bookingConfirmationFlow: BookingConfirmationFlow,
        public bookingToken?: string,
    ) {}

    get requestToBook() {
        return this.bookingConfirmationFlow === BookingConfirmationFlow.RequestToBook
    }
}
